import axios from 'axios';

/**
 * Visitor attribution for gallery analytics.
 *
 * Each browser gets an anonymous id so the admin can count link opens and join
 * them to a person once they enter their details. Downloads, searches and ZIP
 * requests are recorded server-side by the endpoints that perform them; these
 * helpers only say who is asking. Storage can be blocked (private mode, site
 * data off) — tracking then quietly does nothing and the gallery still works.
 */
const VISITOR_KEY = 'tiq_visitor_id';

const newVisitorId = () =>
  (window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`);

export const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = newVisitorId();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

// Staff previewing a client link from a logged-in admin browser aren't visitors.
const isStaffBrowser = () => {
  try {
    return !!localStorage.getItem('token');
  } catch {
    return false;
  }
};

export const trackVisit = (trackUrl, extra = {}) => {
  const visitorId = getVisitorId();
  if (!visitorId || isStaffBrowser()) return;
  axios.post(trackUrl, { type: 'visit', visitor_id: visitorId, ...extra }).catch(() => {});
};

export const trackIdentify = (trackUrl, { name, email }) => {
  if (!email) return;
  axios.post(trackUrl, { type: 'identify', visitor_id: getVisitorId(), name, email }).catch(() => {});
};

/** How an iPhone/iPad share sheet for a downloaded photo ended ("shared" or "cancelled"). */
export const trackSave = (trackUrl, { name, email, photoId, outcome }) => {
  if (outcome !== 'shared' && outcome !== 'cancelled') return;
  axios.post(trackUrl, {
    type: 'save', visitor_id: getVisitorId(), name, email, photo_id: photoId, outcome,
  }).catch(() => {});
};

/** Query string that attributes a single-photo download to the visitor asking for it. */
export const visitorQuery = ({ name, email }) => {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (name) params.set('name', name);
  const visitorId = getVisitorId();
  if (visitorId) params.set('vid', visitorId);
  return params.toString();
};

/** Adds who-is-searching fields to a face-search upload. */
export const appendVisitorFields = (formData, { name, email } = {}, fieldNames = {}) => {
  const { emailField = 'user_email', nameField = 'user_name' } = fieldNames;
  if (email) formData.append(emailField, email);
  if (name) formData.append(nameField, name);
  const visitorId = getVisitorId();
  if (visitorId) formData.append('visitor_id', visitorId);
};
